<script lang="ts" setup>
  import { Text, BaseSelect } from '@bt/design-system'
  import { useFetchInvestorEvents } from '@/composables/useFetchInvestorEvents'
  import type ICard from '~/types/Storyblok/General/Card'

  defineProps<{
    blok: any
  }>()

  const { fetchInvestorEvents } = useFetchInvestorEvents()
  const { locale, t } = useI18n()

  const futureEvents = shallowRef({})
  const pastEvents = shallowRef({})
  const rawEventsData = ref({})
  const canLoadMore = ref(false)

  const getEvents = async () => {
    return await fetchInvestorEvents(locale.value)
  }

  const filterByEventType = (events: ICard[]) => {
    if (!activeFilter.value.filterKeys.length) return []

    const filteredByEventType = events.filter((event: ICard) =>
      activeFilter.value.filterKeys.includes(event.content.eventType ?? 'unknown')
    )

    if (activeFilter.value.key === FILTER_VIDEO_RECORDING) {
      return filteredByEventType.filter((event) => event.content.hasVideoRecording)
    }

    return filteredByEventType
  }

  const chunkObject = (object: any) => {
    if (!object) {
      return {}
    }

    const keys = Object.keys(object)
    const chunkedObjects = {}

    keys.forEach((key) => {
      const events = filterByEventType(object[key])

      if (events.length) {
        Object.assign(chunkedObjects, { [key]: events })
      }
    })

    return chunkedObjects
  }

  const clearEvents = () => {
    futureEvents.value = {}
    pastEvents.value = {}
    canLoadMore.value = false
  }

  const setEventsData = (data: any) => {
    if (!data) {
      clearEvents()
      return
    }

    futureEvents.value = chunkObject(data.events.futureEvents)
    pastEvents.value = chunkObject(data.events.pastEvents)
  }

  onMounted(async () => {
    const events = await getEvents()
    rawEventsData.value = events ?? {
      events: {
        futureEvents: {},
        pastEvents: {}
      }
    }

    setEventsData(events)
  })

  const FILTER_ALL = 'all'
  const FILTER_EVENTS = 'event'
  const FILTER_ANNOUNCMENTS = 'announcement'
  const FILTER_VIDEO_RECORDING = 'video-recording'

  const EVENT_FILTER_OPTIONS = [
    { key: FILTER_ALL, title: t('reusable.all'), filterKeys: [FILTER_EVENTS, FILTER_ANNOUNCMENTS] },
    { key: FILTER_EVENTS, title: t('investor-events.event', 2), filterKeys: [FILTER_EVENTS] },
    { key: FILTER_ANNOUNCMENTS, title: t('investor-events.announcement', 2), filterKeys: [FILTER_ANNOUNCMENTS] },
    {
      key: FILTER_VIDEO_RECORDING,
      title: t('investor-events.video-recording'),
      filterKeys: [FILTER_EVENTS, FILTER_ANNOUNCMENTS]
    }
  ]

  const activeFilter = ref(EVENT_FILTER_OPTIONS[0])
  const onDropdownItemChange = (item: any) => {
    activeFilter.value = item
    setEventsData(rawEventsData.value)
  }
</script>

<template>
  <div class="investor-events">
    <div class="investor-events__filter">
      <Text
        type="body1"
        :text="t('investor-events.show-investor-events')"
        tag="p"
      />
      <BaseSelect
        :items="EVENT_FILTER_OPTIONS"
        :mobile-label="t('investor-events.select-filter')"
        :placeholder="t('investor-events.select-filter')"
        :active-item="activeFilter"
        class="investor-events__dropdown"
        @on-change="onDropdownItemChange"
      />
    </div>
    <div class="investor-events__future">
      <Text
        type="h3"
        :text="t('reusable.futureEvents')"
        class="investor-events__title"
        tag="h3"
      />
      <EventCards
        v-if="Object.keys(futureEvents).length"
        :blok="futureEvents"
      />
      <Text
        v-else
        type="body2"
        :text="$t('investor-events.no-events')"
        tag="p"
      />
    </div>
    <div class="investor-events__past">
      <Text
        type="h3"
        :text="t('reusable.pastEvents')"
        class="investor-events__title"
        tag="h3"
      />
      <EventCards
        v-if="Object.keys(pastEvents).length"
        :blok="pastEvents"
        show-accordion
        class="investor-events__reverse-order"
      />
      <Text
        v-else
        type="body2"
        :text="$t('investor-events.no-events')"
        tag="p"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use '@/assets/styles/common.scss' as *;

  .investor-events {
    &__filter {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: rem(8);
      padding: rem(16) rem(16) rem(24) rem(16);
      background: $bg-grey;
      border-radius: 8px;
      margin-bottom: rem(32);

      @include desktop-up {
        flex-direction: row;
        gap: rem(24);
        padding: rem(24) rem(32);
        margin-bottom: rem(40);
      }
    }

    &__dropdown {
      width: 100%;

      @include desktop-up {
        max-width: 230px;
      }
    }

    &__future {
      margin-bottom: rem(40);
    }

    &__title {
      margin-bottom: rem(24);
    }

    &__reverse-order {
      display: flex;
      flex-direction: column-reverse;
    }
  }
</style>
